// =============================================================================
// Journey client-interaction telemetry (analytics).
//
// Fire-and-forget, batched emitter for per-screen UX metrics:
//   loader time, time-to-first-action, time-to-submit, screen re-views.
//
// Hard contract: telemetry MUST NEVER block or break the user journey. Every
// public function is wrapped so a thrown error, a missing JWT, a dead network,
// or a non-2xx response is swallowed silently — no user-visible effect.
//
// Backend contract (FIXED):
//   POST {baseUrl}/journey/telemetry   (baseUrl already includes /api/v1) (JWT Bearer, X-Session-Id)
//   body: { events: [ {
//     leadId, sessionId, stage, screenId, eventType, platform,
//     deviceTimestamp, clientSeq, loaderMs?, prevScreenId?, metadata
//   } ] }
//   eventType — BE allow-list (analytics taxonomy v3.3, FE-sourced events):
//     screen lifecycle:  screen_view | screen_loaded | first_interaction |
//                        submit_clicked | screen_exit | back_nav
//     interaction:       cta_clicked | field_focused | field_completed
//     session:           session_started | app_backgrounded | app_foregrounded
//     OTP UX:            otp_resend_requested
//     permissions:       location_permission_prompted | location_permission_result
//     vendor SDK:        sdk_invoked | sdk_closed
//   Unknown types are silently dropped by the BE. screenId/prevScreenId/platform
//   must match ^[A-Za-z0-9_.-]{1,100}$ or the BE drops the event whole; batches
//   cap at 200 events; metadata over 4KB is nulled server-side.
//   clientSeq: monotonic per-page-load counter — the BE's intra-batch ordering
//   key (all events of one flush batch share one server_timestamp).
//   stage: resolved from SCREEN_STAGE (mirror of the BE screen_catalog table)
//   first — the redux currentStage lags the visible screen and produced wrong
//   stage tags (verifyEmail rows tagged stage 2 in dev data).
//   Response: 202 (ignored). Failures are non-fatal.
//
//   sdk_invoked: emitted once per liveness/selfie SDK launch. metadata carries
//   { vendor: 'HYPERVERGE' | 'AINXT', locationPermission, cameraPermission }
//   where each permission ∈ granted | denied | prompt | unknown. Lets us count
//   how often each vendor is launched WITHOUT location and WITHOUT camera
//   permission (tracked separately). sdk_closed pairs with it on SDK return
//   (metadata { vendor, status }), so opens/closes and abandonment are countable.
//
// apiBaseUrl + JWT + sessionId are read exactly the way the rest of the API
// client reads them: baseUrl from endpoints.baseUrl (REACT_APP_BASE_URL),
// JWT from journey.getJwt(), sessionId from journey.getSessionId(). leadId is
// the numeric userId (JWT `sub` claim) when present, else the leadId Guid.
// =============================================================================

import { endpoints } from './endpoints';
import { getJwt, getSessionId, getUserId, getLeadId } from './journey';

// baseUrl (endpoints.baseUrl / REACT_APP_BASE_URL) already ends in /api/v1,
// so the path is relative like every other endpoint (e.g. '/journey/status').
// Using an absolute '/api/v1/...' here doubled it to /api/v1/api/v1/... → 404.
const TELEMETRY_PATH = '/journey/telemetry';
const PLATFORM = 'WEB';
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 100;
// Mirror of TelemetryIngestService.MaxBatchSize. Exceeding it drops events
// silently server-side.
const MAX_BATCH = 200;

// --- pre-auth buffer ---------------------------------------------------------
// Stages 1-2 run before any JWT. Their events are buffered here and replayed
// after OTP verify, so every row reaches the BE with a real userId and the
// endpoint never needs an anonymous path.
//
// LIMITATION (by design): a user who abandons before OTP verify never flushes,
// so their events are lost. The ARRIVAL COUNT is still available server-side
// from lead_sessions (user_id NULL = a landing that never signed up); what is
// lost is the within-page behaviour of abandoners.
const PREAUTH_KEY = 'ekyc_preauth_telemetry';
// Which lead the buffer belongs to. sessionStorage survives an in-tab journey
// restart (index.js only calls /journey/init when no sessionId exists, so the
// session is REUSED), and browsers restore it on "reopen closed tab" / session
// restore. Without an owner check, attempt 1's events would replay under
// attempt 2's userId — on a shared or agent-assisted device, that means one
// person's events landing under another person's lead.
const PREAUTH_OWNER_KEY = 'ekyc_preauth_telemetry_owner';
// When the current buffer started accumulating (ms epoch, device clock).
const PREAUTH_STARTED_KEY = 'ekyc_preauth_telemetry_started';
const PREAUTH_MAX = 300;
// Matches the OTP session TTL. A buffer older than this cannot belong to the
// journey now completing — drop it rather than replay stale events.
const PREAUTH_MAX_AGE_MS = 30 * 60 * 1000;

const readPreAuthBuffer = () => {
    try {
        const raw = sessionStorage.getItem(PREAUTH_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
};

const writePreAuthBuffer = (events) => {
    try {
        sessionStorage.setItem(PREAUTH_KEY, JSON.stringify(events));
    } catch (_) {
        // Quota exceeded / storage disabled — telemetry is best-effort.
    }
};

const bufferPreAuth = (evt) => {
    try {
        const buf = readPreAuthBuffer();
        if (buf.length === 0 && !sessionStorage.getItem(PREAUTH_STARTED_KEY)) {
            sessionStorage.setItem(PREAUTH_STARTED_KEY, String(Date.now()));
        }
        buf.push(evt);
        // Drop oldest on overflow, matching the live queue's MAX_QUEUE policy.
        writePreAuthBuffer(buf.length > PREAUTH_MAX ? buf.slice(buf.length - PREAUTH_MAX) : buf);
    } catch (_) {
        /* swallow */
    }
};

let _queue = [];
let _timer = null;
let _beforeUnloadBound = false;
let _clientSeq = 0;
let _sessionStartedSent = false;

// Mirror of the BE ekyc.screen_catalog table: screenId (lowercased) → journey
// stage (0-15). The redux journey.currentStage lags the visible screen, so the
// stage a caller passes is only a fallback. Keep in sync with the catalog seed
// in migration AddUserTelemetryPhase1 (mofsl-ekyc-backend).
const SCREEN_STAGE = {
    verifyemail: 3,
    pan: 4,
    adhaar: 5,
    bank: 6,
    signature: 7,
    livephoto: 8,
    personalandnominee: 9,
    fatherspouse: 9,
    tradeinfno: 9,
    incomeproof: 10,
    adhaarphotoincomeproof: 10,
    onemoney: 10,
    verifyesign: 13,
    esign: 13,
    nonstp: 13,
    rejectioncorrection: 13,
    congratulation: 14,
    // Stage 16 — post-eSign F&O. A pseudo-stage: it exists for telemetry and
    // screen_catalog only and never moves the lead's current_stage. Keys are
    // lower-cased because resolveStage lower-cases the screenId before lookup.
    fnooffer: 16,
    fnobankconfirm: 16,
    fnofetching: 16,
    fnosubmitted: 16,
    fnotrylater: 16,
    // Stage 17 — post-eSign SSP (Super Saving Plan) card, its own pseudo-stage like F&O's 16
    // (BE StageDefinitions.PostEsignSsp, screen_catalog seeded by AddSspStage17ScreenCatalog).
    // The number is not an order: the cross-sell layer may show SSP before F&O.
    ssp: 17,
    sspprocessing: 17,
    sspsuccess: 17,
    sspfailure: 17,
};

const resolveStage = (stage, screenId) => {
    try {
        const mapped = screenId != null ? SCREEN_STAGE[String(screenId).toLowerCase()] : undefined;
        if (mapped != null) return mapped;
        return stage != null && stage !== '' ? Number(stage) : null;
    } catch (_) {
        return null;
    }
};

const nowIso = () => {
    try {
        return new Date().toISOString();
    } catch (_) {
        return null;
    }
};

// leadId in the contract is numeric (e.g. 12345). Prefer the numeric userId
// from the JWT sub claim; fall back to whatever leadId we hold. Coerce a pure
// digit string to a Number so it serialises as a JSON number.
const resolveLeadId = () => {
    try {
        const uid = getUserId();
        if (uid != null && uid !== '' && /^\d+$/.test(String(uid))) return Number(uid);
        const lid = getLeadId();
        if (lid != null && lid !== '' && /^\d+$/.test(String(lid))) return Number(lid);
        return lid != null ? lid : null;
    } catch (_) {
        return null;
    }
};

const buildEvent = (eventType, stage, screenId, extra = {}) => {
    _clientSeq += 1;
    const evt = {
        leadId: resolveLeadId(),
        sessionId: getSessionIdSafe(),
        stage: resolveStage(stage, screenId),
        screenId: screenId != null ? String(screenId) : null,
        eventType,
        platform: PLATFORM,
        deviceTimestamp: nowIso(),
        clientSeq: _clientSeq,
        metadata: {},
    };
    if (extra.loaderMs != null && !Number.isNaN(extra.loaderMs)) {
        evt.loaderMs = Math.max(0, Math.round(extra.loaderMs));
    }
    if (extra.prevScreenId != null) evt.prevScreenId = String(extra.prevScreenId);
    if (extra.metadata && typeof extra.metadata === 'object') evt.metadata = extra.metadata;
    return evt;
};

const getSessionIdSafe = () => {
    try {
        return getSessionId();
    } catch (_) {
        return null;
    }
};

const ensureTimer = () => {
    if (_timer || typeof window === 'undefined') return;
    try {
        _timer = window.setInterval(() => {
            flush();
        }, FLUSH_INTERVAL_MS);
    } catch (_) {
        _timer = null;
    }
};

const ensureBeforeUnload = () => {
    if (_beforeUnloadBound || typeof window === 'undefined') return;
    try {
        // pagehide is more reliable than beforeunload on mobile Safari; bind both.
        const handler = () => {
            try {
                flush(true);
            } catch (_) {
                /* swallow */
            }
        };
        window.addEventListener('pagehide', handler);
        window.addEventListener('beforeunload', handler);
        _beforeUnloadBound = true;
    } catch (_) {
        _beforeUnloadBound = true; // don't retry forever
    }
};

// One-shot latch: the first authenticated event drains any stage 1-2 buffer
// left behind when the OTP-verify replay ran before the JWT was stored.
let _preAuthDrained = false;

const enqueue = (evt) => {
    try {
        if (!evt) return;
        // No JWT yet (stage 1-2): buffer instead of sending. The BE endpoint is
        // [Authorize]-only, so an unauthenticated POST would just 401 and the
        // event would be lost.
        if (!getJwtSafe()) {
            // TEMP DEBUG — remove once verified in prod console. Stage 1-2 events
            // buffer here (no JWT yet) and only reach the network after OTP
            // verify replays the buffer — this log is the only local signal
            // that they fired at all before that point.
            console.log(`[telemetry] buffered pre-auth (no JWT yet): ${evt.eventType}`, evt);
            bufferPreAuth(evt);
            return;
        }
        // JWT now exists. The OTP-verify replay (flushPreAuthBuffer) may have
        // fired before the JWT was stored and no-op'd to preserve the buffer.
        // Drain any leftover stage 1-2 events once, now that the replay can
        // authenticate. Same-tab sessionStorage survived the nav into /dad.
        if (!_preAuthDrained) {
            _preAuthDrained = true;
            flushPreAuthBuffer();
        }
        _queue.push(evt);
        if (_queue.length > MAX_QUEUE) {
            // Drop oldest — telemetry is best-effort, never let the buffer grow.
            _queue.splice(0, _queue.length - MAX_QUEUE);
        }
        ensureTimer();
        ensureBeforeUnload();
    } catch (_) {
        /* swallow */
    }
};

// Single POST of an explicit event array. Shared by the live flush and the
// pre-auth replay. Always fire-and-forget: never surfaces an error.
const postBatch = (events) => {
    try {
        if (!events || events.length === 0) return;
        const baseUrl = endpoints.baseUrl;
        if (!baseUrl) return;
        const jwt = getJwtSafe();
        const sessionId = getSessionIdSafe();
        const headers = { 'Content-Type': 'application/json' };
        if (jwt) headers.Authorization = `Bearer ${jwt}`;
        if (sessionId) headers['X-Session-Id'] = sessionId;
        fetch(`${baseUrl}${TELEMETRY_PATH}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ events }),
            credentials: 'omit',
            keepalive: true,
        }).catch(() => { /* swallow */ });
    } catch (_) {
        /* swallow */
    }
};

/**
 * Flush the queued events to the backend. Best-effort: fetch() with keepalive,
 * which the browser lets complete across navigation AND during page teardown
 * (pagehide/beforeunload) — unlike sendBeacon it can carry the Authorization
 * header. The old sendBeacon unload path passed the JWT as an access_token
 * query param; the BE (correctly) never accepted query-string tokens — a JWT
 * in a URL leaks into ALB/audit logs — so those batches 401'd silently. Old
 * call sites passing `flush(true)` remain valid (the arg is ignored).
 * Any failure is swallowed; the queue is cleared regardless so we never spin.
 */
export const flush = () => {
    try {
        if (_queue.length === 0) return;
        const events = _queue;
        _queue = [];
        postBatch(events);
    } catch (_) {
        _queue = [];
    }
};

const getJwtSafe = () => {
    try {
        return getJwt();
    } catch (_) {
        return null;
    }
};

// --- Public API --------------------------------------------------------------
// Every entry point is individually try/caught: a telemetry bug can never
// throw into a journey component's render or event handler.

export const trackScreenView = (stage, screenId, opts = {}) => {
    try {
        enqueue(buildEvent('screen_view', stage, screenId, { prevScreenId: opts.prevScreenId, metadata: opts.metadata }));
    } catch (_) {
        /* swallow */
    }
};

export const trackScreenLoaded = (stage, screenId, loaderMs, opts = {}) => {
    try {
        enqueue(buildEvent('screen_loaded', stage, screenId, { loaderMs, metadata: opts.metadata }));
    } catch (_) {
        /* swallow */
    }
};

// --- Phase 1 named per-page events ------------------------------------------
// The event NAME is the eventType on the wire (e.g. 'pan_verified_submitted').
// screenId is still sent (route segment) so the BE screen_catalog stage lookup
// and the existing indexes keep working. Everything variant-shaped — vendor,
// method, error_code, reason — goes in metadata, never into the name.
//
// Names must match TelemetryIngestService.ValidEventTypes EXACTLY. The BE drops
// unknown types SILENTLY, so a typo here is invisible in production.

// Generic escape hatch for the handful of names that are not <page>_page_<verb>.
export const trackNamed = (eventType, stage, screenId, opts = {}) => {
    try {
        enqueue(buildEvent(eventType, stage, screenId, {
            loaderMs: opts.loaderMs,
            prevScreenId: opts.prevScreenId,
            metadata: opts.metadata,
        }));
    } catch (_) {
        /* swallow */
    }
};

export const trackPageLoaded = (pageName, stage, screenId, loaderMs, opts = {}) => {
    if (!pageName) return;
    // TEMP DEBUG — remove once verified in prod console.
    console.log(`[telemetry] ${pageName}_page_loaded`, { stage, screenId });
    trackNamed(`${pageName}_page_loaded`, stage, screenId, { loaderMs, metadata: opts.metadata });
};

export const trackPageViewed = (pageName, stage, screenId, loaderMs, opts = {}) => {
    if (!pageName) return;
    // TEMP DEBUG — remove once verified in prod console.
    console.log(`[telemetry] ${pageName}_page_viewed`, { stage, screenId });
    trackNamed(`${pageName}_page_viewed`, stage, screenId, {
        loaderMs, prevScreenId: opts.prevScreenId, metadata: opts.metadata,
    });
};

// Fires on the REQUEST, not the response, so click-to-API-failure drop-off
// stays visible. Pass the full event name: the submit suffix is not uniform
// (pan_manual_details_submitted, bank_manual_bank_details_submitted, ...).
export const trackSubmitted = (eventName, stage, screenId, opts = {}) => {
    if (!eventName) return;
    trackNamed(eventName, stage, screenId, { metadata: opts.metadata });
};

export const trackFirstInteraction = (stage, screenId, opts = {}) => {
    try {
        enqueue(buildEvent('first_interaction', stage, screenId, { metadata: opts.metadata }));
    } catch (_) {
        /* swallow */
    }
};

export const trackSubmitClicked = (stage, screenId, opts = {}) => {
    try {
        enqueue(buildEvent('submit_clicked', stage, screenId, { metadata: opts.metadata }));
    } catch (_) {
        /* swallow */
    }
};

export const trackScreenExit = (stage, screenId, opts = {}) => {
    try {
        enqueue(buildEvent('screen_exit', stage, screenId, { metadata: opts.metadata }));
        // A screen change is a good moment to push what we have.
        flush();
    } catch (_) {
        /* swallow */
    }
};

export const trackBackNav = (stage, screenId, opts = {}) => {
    try {
        enqueue(buildEvent('back_nav', stage, screenId, { metadata: opts.metadata }));
    } catch (_) {
        /* swallow */
    }
};

// One event per liveness/selfie SDK launch. Pass metadata =
// { vendor, locationPermission, cameraPermission } so we can count launches
// without location / without camera permission, per vendor and independently.
export const trackSdkInvoked = (stage, screenId, opts = {}) => {
    try {
        enqueue(buildEvent('sdk_invoked', stage, screenId, { metadata: opts.metadata }));
    } catch (_) {
        /* swallow */
    }
};

// Pairs with sdk_invoked when the SDK returns control. Pass metadata =
// { vendor, status } (status e.g. auto_approved / user_cancelled / error) so
// open-vs-close and in-SDK abandonment are countable per vendor.
export const trackSdkClosed = (stage, screenId, opts = {}) => {
    try {
        enqueue(buildEvent('sdk_closed', stage, screenId, { metadata: opts.metadata }));
    } catch (_) {
        /* swallow */
    }
};

// --- Generic pagewise vendor-SDK lifecycle / fetch-result events ------------
// Reusable across ANY page+vendor combo (not just bank/HyperVerge) — the event
// NAME is built as `${pageName}_${sdkName}_sdk_invoked|sdk_closed` and
// `${pageName}_${sdkName}_fetch_success|fetch_failed`. Same closed-allow-list
// rule as every other named event: each concrete name this produces MUST be
// added to TelemetryIngestService.ValidEventTypes (BE) before use, or the BE
// drops it silently. Currently registered there:
//   bank_hyperverge_{sdk_invoked,sdk_closed,fetch_success,fetch_failed}
//     (Stage 6 HyperVerge reverse-penny-drop, see dadBank)
//   selfie_verification_{ainxt,hyperverge}_{sdk_invoked,sdk_closed,
//     fetch_success,fetch_failed}
//     (Stage 8 selfie/liveness — cascades AINXT/HYPERVERGE per attempt, see
//     dadLivePhotoSection)
//   digilocker_{ainxt,digio}_{sdk_invoked,sdk_closed,fetch_success,
//     fetch_failed}
//     (Stage 5 DigiLocker — BE picks vendor dynamically per
//     /aadhaar/digilocker/initiate response, see dadadhaar
//     handleAinxtDigilockerRequest/launchDigioSdk)
// Add the new literal(s) to the BE allow-list before wiring up another
// page/vendor.

// Pairs sdk_invoked (SDK launch) with sdk_closed (SDK returned control) under
// one call, keyed by `action`. Mirrors the generic trackSdkInvoked/
// trackSdkClosed above, but with a named, page+vendor-scoped event type so
// per-page-per-vendor funnels are queryable without a metadata filter.
export const trackPagewiseSdkEvent = (action, pageName, sdkName, stage, screenId, opts = {}) => {
    if (!pageName || !sdkName) return;
    if (action !== 'invoked' && action !== 'closed') return;
    const eventType = `${pageName}_${sdkName}_sdk_${action}`;
    // TEMP DEBUG — remove once verified in prod console. Lets you confirm the
    // event actually fires (and with what name/metadata) without digging
    // through the network tab.
    console.log('[telemetry] trackPagewiseSdkEvent ->', eventType, { stage, screenId, metadata: opts.metadata });
    trackNamed(eventType, stage, screenId, { metadata: opts.metadata });
};

// Result of a page's post-SDK "fetch the verified details" step (e.g. bank
// details after HyperVerge returns AUTO_APPROVED). `success` picks
// fetch_success vs fetch_failed.
export const trackPagewiseFetchEvent = (success, pageName, sdkName, stage, screenId, opts = {}) => {
    if (!pageName || !sdkName) return;
    const eventType = `${pageName}_${sdkName}_fetch_${success ? 'success' : 'failed'}`;
    // TEMP DEBUG — remove once verified in prod console.
    console.log('[telemetry] trackPagewiseFetchEvent ->', eventType, { stage, screenId, metadata: opts.metadata });
    trackNamed(eventType, stage, screenId, { metadata: opts.metadata });
};

// Any button-ish tap, repeatable (unlike the once-per-screen submit_clicked).
// Label goes in metadata (the BE identifier regex only applies to screenId /
// prevScreenId / platform — metadata is free-form).
export const trackCtaClick = (stage, screenId, ctaLabel) => {
    try {
        enqueue(buildEvent('cta_clicked', stage, screenId, { metadata: { ctaLabel: ctaLabel || null } }));
    } catch (_) {
        /* swallow */
    }
};

// Field NAME only — never the value (PII).
export const trackFieldFocused = (stage, screenId, fieldName) => {
    try {
        enqueue(buildEvent('field_focused', stage, screenId, { metadata: { field: fieldName || null } }));
    } catch (_) {
        /* swallow */
    }
};

export const trackFieldCompleted = (stage, screenId, fieldName) => {
    try {
        enqueue(buildEvent('field_completed', stage, screenId, { metadata: { field: fieldName || null } }));
    } catch (_) {
        /* swallow */
    }
};

// Once per page load (module guard) — many sessions map to one journey.
export const trackSessionStarted = (stage, screenId) => {
    try {
        if (_sessionStartedSent) return;
        _sessionStartedSent = true;
        enqueue(buildEvent('session_started', stage, screenId));
    } catch (_) {
        /* swallow */
    }
};

export const trackAppBackgrounded = (stage, screenId) => {
    try {
        enqueue(buildEvent('app_backgrounded', stage, screenId));
        // The tab may never come back — push what we have while we still can.
        flush();
    } catch (_) {
        /* swallow */
    }
};

export const trackAppForegrounded = (stage, screenId, opts = {}) => {
    try {
        enqueue(buildEvent('app_foregrounded', stage, screenId, { metadata: opts.metadata }));
    } catch (_) {
        /* swallow */
    }
};

export const trackOtpResendRequested = (stage, screenId, opts = {}) => {
    try {
        enqueue(buildEvent('otp_resend_requested', stage, screenId, { metadata: opts.metadata }));
    } catch (_) {
        /* swallow */
    }
};

// Location permission funnel: emit `..._prompted` just before a call that can
// show the browser dialog (only meaningful when the pre-call state is
// 'prompt'), and `..._result` with metadata { result: granted | denied |
// unavailable } once the call settles.
export const trackLocationPermissionPrompted = (stage, screenId) => {
    try {
        enqueue(buildEvent('location_permission_prompted', stage, screenId));
    } catch (_) {
        /* swallow */
    }
};

export const trackLocationPermissionResult = (stage, screenId, result) => {
    try {
        enqueue(buildEvent('location_permission_result', stage, screenId, { metadata: { result: result || 'unknown' } }));
    } catch (_) {
        /* swallow */
    }
};

// Replay the buffered stage 1-2 events once a JWT exists (called right after
// OTP verify dispatches setUser).
//
// CHUNKING IS MANDATORY: the BE caps a batch at 200 events
// (TelemetryIngestService.MaxBatchSize) and skips the overflow SILENTLY, with
// no error. An unchunked replay of a long pre-auth session loses events with
// no trace.
//
// Replayed events keep their original clientSeq and deviceTimestamp. Their
// server_timestamp is the replay moment, so ANY analysis of stages 1-2 must
// key on deviceTimestamp, never server_timestamp.
//
// deviceTimestamp is already UTC (new Date().toISOString() always emits a Z
// suffix regardless of device locale) and the column is timestamptz, so there
// is NO timezone conversion to do. What UTC does not fix is clock SKEW: a
// device with a wrong clock yields a wrong UTC value. So each replayed event is
// stamped with:
//   replayed:       true      -- so these rows are never silently mixed with live ones
//   replay_lag_ms:  number    -- how long before the replay it was recorded, on
//                                the DEVICE clock
// Durations within a session are already skew-immune (one clock throughout).
// For absolute time, use (server_timestamp - replay_lag_ms), which is anchored
// on our clock and therefore skew-free.
export const flushPreAuthBuffer = () => {
    try {
        const buffered = readPreAuthBuffer();
        if (buffered.length === 0) return 0;

        // Staleness guard: a buffer older than the OTP session TTL cannot belong
        // to the journey now completing. Covers the tab-restore case, where the
        // browser resurrects sessionStorage from a much earlier session.
        // startedAt and Date.now() come from the same clock, so this is
        // skew-immune.
        const startedAt = Number(sessionStorage.getItem(PREAUTH_STARTED_KEY));
        if (startedAt && Date.now() - startedAt > PREAUTH_MAX_AGE_MS) {
            discardPreAuthBuffer();
            return 0;
        }

        // The JWT is often NOT stored yet at the OTP-verify call site (it is set
        // asynchronously by the auth transport). Replaying now would POST the
        // [Authorize]-only /journey/telemetry endpoint with no Bearer token ->
        // 401 -> the (discarded) stage 1-2 events are lost forever. Preserve the
        // buffer instead; enqueue() drains it on the first authenticated event
        // once the JWT exists. Do NOT discard here — this is a retry-later path.
        if (!getJwtSafe()) return 0;

        const replayAt = Date.now();
        const stamped = buffered.map((evt) => {
            try {
                const recordedAt = evt.deviceTimestamp ? Date.parse(evt.deviceTimestamp) : NaN;
                const lag = Number.isNaN(recordedAt) ? null : Math.max(0, replayAt - recordedAt);
                return {
                    ...evt,
                    metadata: { ...(evt.metadata || {}), replayed: true, replay_lag_ms: lag },
                };
            } catch (_) {
                return evt;
            }
        });

        discardPreAuthBuffer();

        for (let i = 0; i < stamped.length; i += MAX_BATCH) {
            postBatch(stamped.slice(i, i + MAX_BATCH));
        }
        return stamped.length;
    } catch (_) {
        return 0;
    }
};

// Drop the buffer without sending — journey reset / logout / lead mismatch.
export const discardPreAuthBuffer = () => {
    try {
        sessionStorage.removeItem(PREAUTH_KEY);
        sessionStorage.removeItem(PREAUTH_OWNER_KEY);
        sessionStorage.removeItem(PREAUTH_STARTED_KEY);
    } catch (_) {
        /* swallow */
    }
};

// Claim the buffer for a lead. Call on /registration/signup success — the first
// moment an identity exists.
//
// If the buffer already belongs to a DIFFERENT lead it is a previous person's
// (or a previous attempt with a different mobile) and must NOT be replayed under
// this userId — discard it. The SAME leadId is kept deliberately: that is one
// person retrying, and both attempts are legitimately theirs.
export const bindPreAuthBufferToLead = (leadId) => {
    try {
        if (leadId == null || leadId === '') return;
        const owner = sessionStorage.getItem(PREAUTH_OWNER_KEY);
        if (owner && String(owner) !== String(leadId)) {
            discardPreAuthBuffer();
        }
        sessionStorage.setItem(PREAUTH_OWNER_KEY, String(leadId));
    } catch (_) {
        /* swallow */
    }
};

const telemetry = {
    trackScreenView,
    trackScreenLoaded,
    trackNamed,
    trackPageLoaded,
    trackPageViewed,
    trackSubmitted,
    trackFirstInteraction,
    trackSubmitClicked,
    trackScreenExit,
    trackBackNav,
    trackSdkInvoked,
    trackSdkClosed,
    trackPagewiseSdkEvent,
    trackPagewiseFetchEvent,
    trackCtaClick,
    trackFieldFocused,
    trackFieldCompleted,
    trackSessionStarted,
    trackAppBackgrounded,
    trackAppForegrounded,
    trackOtpResendRequested,
    trackLocationPermissionPrompted,
    trackLocationPermissionResult,
    flush,
    flushPreAuthBuffer,
    discardPreAuthBuffer,
    bindPreAuthBufferToLead,
};

export default telemetry;
